/** Academy back office: small helpers shared by the tabs (roles, refresh, errors, statuses, pager). */

import { ApiError, type ContentStatusEnum } from "@agencyos/api-client";
import { Badge, type BadgeTone } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { fieldErrors, useCurrentRole } from "../../lib/settings";
import { Pager as BasePager } from "../billing/shared";

export const TRAINER_ROLES = ["owner", "manager", "trainer"] as const;
export const LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"] as const;
export const CONTENT_STATUSES: ContentStatusEnum[] = ["draft", "reviewed", "published", "archived"];

const STATUS_TONE: Record<ContentStatusEnum, BadgeTone> = {
  draft: "default",
  reviewed: "offer",
  published: "success",
  archived: "danger",
};

/** Owner, manager and trainer write the content; the advisor reads it. */
export function useAcademyRoles() {
  const role = useCurrentRole();
  return {
    role,
    canEdit: role !== undefined && (TRAINER_ROLES as readonly string[]).includes(role),
    /** The plans and prices are the owner's and manager's (backend: write_roles). */
    canEditSettings: role === "owner" || role === "manager",
  };
}

export function useRefreshAcademy() {
  const queryClient = useQueryClient();
  return () =>
    void queryClient.invalidateQueries({
      predicate: (query) => String(query.queryKey[0] ?? "").startsWith("/api/v1/academy/"),
    });
}

/** Every message of a DRF error, for actions whose errors are not tied to one field. */
export function errorMessages(error: unknown, fallback: string): string[] {
  if (!error) return [];
  const fields = Object.values(fieldErrors(error));
  if (fields.length) return fields;
  if (error instanceof ApiError && error.detail) return [error.detail];
  return [fallback];
}

export function ErrorList({ error }: { error: unknown }) {
  const { t } = useTranslation();
  const messages = errorMessages(error, t("common.error"));
  if (!messages.length) return null;
  return (
    <div role="alert" className="ao-field__error stack">
      {messages.map((m) => (
        <span key={m}>{m}</span>
      ))}
    </div>
  );
}

export function StatusBadge({ status }: { status: ContentStatusEnum }) {
  const { t } = useTranslation();
  return <Badge tone={STATUS_TONE[status]}>{t(`academyAdmin.status.${status}`)}</Badge>;
}

/** 0.42 → « 42 % ». */
export function percent(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return `${Math.round(value * 100)} %`;
}

export function Pager({ page, hasNext, onChange }: { page: number; hasNext: boolean; onChange: (page: number) => void }) {
  const { t } = useTranslation();
  return (
    <BasePager
      page={page}
      hasNext={hasNext}
      onChange={onChange}
      labels={{ previous: t("academyAdmin.previous"), next: t("academyAdmin.next"), page: t("academyAdmin.page", { n: page }) }}
    />
  );
}

export function isObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

export function str(value: unknown): string {
  return typeof value === "string" ? value : typeof value === "number" ? String(value) : "";
}

export function num(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : Number(value) || 0;
}

let rowCounter = 0;
/** Stable React keys for rows added in a form. */
export function nextKey(): number {
  rowCounter += 1;
  return rowCounter;
}

/** Numbers typed with a comma or a dot. */
export function decimalInput(value: string): string {
  return value.replace(",", ".").replace(/[^0-9.]/g, "");
}

export function digits(value: string): string {
  return value.replace(/\D/g, "");
}
